/**
 * useDealChat Hook
 *
 * Custom hook for managing chat state with a deal's knowledge base.
 * Integrates with the RAG chat endpoint.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { captureEvent } from '@/lib/posthog'
import { dealsService } from '../services/deals.service'
import type { ChatMessage, ChatRequest, ChatResponse, ChatStatusResponse, DealUpdateRequest, Deal } from '../types/deal.types'

interface UseDealChatOptions {
  enabled?: boolean
}

export function useDealChat(dealId: string, options: UseDealChatOptions = {}) {
  const { enabled = true } = options
  const queryClient = useQueryClient()

  // Local message state (not persisted to server)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | undefined>()

  // Track if we've already triggered embedding processing to avoid duplicate calls
  const embeddingTriggeredRef = useRef<string | null>(null)

  // Check if chat is ready (embeddings complete)
  const chatStatusQuery = useQuery({
    queryKey: ['deal-chat-status', dealId],
    queryFn: () => dealsService.getChatStatus(dealId),
    enabled: enabled && !!dealId,
    refetchInterval: (query) => {
      const data = query.state.data as ChatStatusResponse | undefined
      // Keep polling while not ready
      return data && !data.is_ready ? 3000 : false
    },
  })

  // Mutation to trigger embedding processing
  const processEmbeddingsMutation = useMutation({
    mutationFn: () => dealsService.processEmbeddings(dealId),
    onSuccess: () => {
      // Invalidate chat status to pick up new embedding status
      queryClient.invalidateQueries({ queryKey: ['deal-chat-status', dealId] })
    },
    onError: (error) => {
      console.error('Failed to process embeddings:', error)
      // DON'T reset the ref on error - this would cause infinite retry loop
      // The ref stays set so we don't keep retrying failed requests
    },
  })

  // Auto-trigger embedding processing when markdown is complete but embeddings are pending
  // NOTE: We use processEmbeddingsMutation.mutate in deps, not the whole mutation object
  const triggerEmbeddings = processEmbeddingsMutation.mutate
  const isEmbeddingsPending = processEmbeddingsMutation.isPending

  useEffect(() => {
    const status = chatStatusQuery.data
    if (!status) return

    // Only trigger if:
    // 1. Markdown is completed
    // 2. Embeddings are pending (not completed, not processing)
    // 3. We haven't already triggered for this deal
    // 4. There are sources to process
    // 5. We're not currently processing
    const shouldTrigger =
      status.markdown_status === 'completed' &&
      status.embedding_status === 'pending' &&
      status.total_sources > 0 &&
      embeddingTriggeredRef.current !== dealId &&
      !isEmbeddingsPending

    if (shouldTrigger) {
      embeddingTriggeredRef.current = dealId
      triggerEmbeddings()
    }
  }, [chatStatusQuery.data, dealId, triggerEmbeddings, isEmbeddingsPending])

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (request: ChatRequest) => dealsService.sendChatMessage(dealId, request),
    onMutate: async (request) => {
      // Optimistically add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: request.message,
        timestamp: new Date(),
      }

      // Add a loading assistant message
      const loadingMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isLoading: true,
      }

      setMessages((prev) => [...prev, userMessage, loadingMessage])

      return { userMessage, loadingMessage }
    },
    onSuccess: (response: ChatResponse, _variables, context) => {
      // Replace loading message with actual response
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === context?.loadingMessage.id
            ? {
                id: msg.id,
                role: 'assistant' as const,
                content: response.response,
                timestamp: new Date(response.timestamp),
                sources: response.sources,
                followUpQuestions: response.follow_up_questions,
                isLoading: false,
              }
            : msg
        )
      )

      // Update conversation ID if returned
      // Note: Backend may return conversation_id in future
    },
    onError: (_error, _variables, context) => {
      // Remove the loading message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== context?.loadingMessage.id))
    },
  })

  // Send a message
  const sendMessage = useCallback(
    (message: string) => {
      if (!message.trim()) return

      const request: ChatRequest = {
        message: message.trim(),
        conversation_id: conversationId,
        max_sources: 5,
      }

      sendMessageMutation.mutate(request)
    },
    [conversationId, sendMessageMutation]
  )

  // Clear chat history
  const clearMessages = useCallback(() => {
    setMessages([])
    setConversationId(undefined)
  }, [])

  // Refresh chat status
  const refreshStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['deal-chat-status', dealId] })
  }, [queryClient, dealId])

  return {
    // Chat status
    isReady: chatStatusQuery.data?.is_ready ?? false,
    chatStatus: chatStatusQuery.data,
    isStatusLoading: chatStatusQuery.isLoading,
    statusError: chatStatusQuery.error,

    // Messages
    messages,
    sendMessage,
    clearMessages,
    isSending: sendMessageMutation.isPending,
    sendError: sendMessageMutation.error,

    // Embedding processing
    isProcessingEmbeddings: processEmbeddingsMutation.isPending,
    embeddingError: processEmbeddingsMutation.error,

    // Utils
    refreshStatus,
  }
}

/**
 * Hook to fetch active deals for sidebar
 */
export function useActiveDeals() {
  return useQuery({
    queryKey: ['deals', 'active'],
    queryFn: async () => {
      // Fetch all deals
      const response = await dealsService.list({
        sort: '-updated_at',
        limit: 100,
      })

      // Filter out closed and passed deals on the client side
      // (backend only supports single stage filter)
      const activeStages = ['new', 'screening', 'partner_meeting', 'diligence', 'term_sheet']
      const activeDeals = response.items.filter((deal) =>
        activeStages.includes(deal.stage)
      )

      return {
        ...response,
        items: activeDeals,
        total: activeDeals.length,
      }
    },
    staleTime: 30000, // 30 seconds
  })
}

/**
 * Hook to fetch a single deal
 */
export function useDeal(dealId: string) {
  return useQuery({
    queryKey: ['deal', dealId],
    queryFn: () => dealsService.getById(dealId),
    enabled: !!dealId,
  })
}

/**
 * Hook to update a deal
 * Invalidates deal query on success for automatic refetch
 */
export function useUpdateDeal(dealId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: DealUpdateRequest) => dealsService.updateDeal(dealId, data),
    onSuccess: (updatedDeal: Deal) => {
      // Update the deal in cache
      queryClient.setQueryData(['deal', dealId], updatedDeal)
      // Also invalidate deal lists that might contain this deal
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
  })
}

/**
 * Hook to update deal stage using PATCH endpoint
 * This auto-triggers Quick Screen when moving to "screening" stage
 * Invalidates diligence queries to pick up new task_id
 */
export function useUpdateDealStage(dealId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ stage, reason }: { stage: string; reason?: string }) =>
      dealsService.updateDealStage(dealId, stage, reason),
    onSuccess: (updatedDeal: Deal, variables) => {
      captureEvent('deal_stage_changed', { deal_id: dealId, to_stage: variables.stage })
      // Update the deal in cache
      queryClient.setQueryData(['deal', dealId], updatedDeal)
      // Invalidate deal lists
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      // Invalidate diligence to pick up new task_id from auto-triggered Quick Screen
      queryClient.invalidateQueries({ queryKey: ['deal-diligence', dealId] })
    },
  })
}

/**
 * Hook to delete a deal
 * Invalidates deal lists on success for automatic refetch
 */
export function useDeleteDeal() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dealId: string) => dealsService.deleteDeal(dealId),
    onSuccess: (_data, dealId) => {
      captureEvent('deal_deleted', { deal_id: dealId })
      // Remove the deal from cache
      queryClient.removeQueries({ queryKey: ['deal', dealId] })
      // Invalidate deal lists to refresh
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
